// src/viqai/AffiliateMember.jsx
// ─── Espace apporteur ───────────────────────────────────────────────────────
// Remplace l'ancien tableau de bord, qui agrégeait les ventes par CODE. Sur un
// code partagé, cela montrait à Marie le total de Thomas : la faute était
// invisible tant qu'un seul apporteur portait un code, elle devient une fuite
// dès le premier binôme. Ici tout vient de /api/affiliate-member/me, agrégé sur
// les répartitions individuelles.
//
// §33 — Un participant voit son rôle, sa part et sa commission. Il voit aussi
// que d'autres participent au code — le cacher serait absurde puisqu'il
// travaille avec eux — mais pas leurs montants.
//
// Le script commercial suit les trois étapes du cycle plutôt que d'empiler des
// blocs : Calling → Qualification → Closing.

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost } from "./api";
import "./viqai-dark.css";

const eur = (c) => `${((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const ROLE_LABELS = {
  calling: "Calling", qualification: "Qualification", closing: "Closing",
  partner: "Partenaire", manager: "Manager", custom: "Autre",
};

// ── Questions de qualification (§60) ────────────────────────────────────────
// Elles servent deux fois : à structurer l'échange, et à alimenter le score.
// C'est pour ça qu'elles portent un poids explicite plutôt qu'un simple texte.
const QUALIF = [
  { id: "volume", label: "Combien de véhicules d'occasion par mois ?", type: "number", weight: 3,
    score: v => (v >= 40 ? 3 : v >= 15 ? 2 : v >= 5 ? 1 : 0) },
  { id: "collaborateurs", label: "Combien de collaborateurs ?", type: "number", weight: 1,
    score: v => (v >= 3 ? 2 : v >= 1 ? 1 : 0) },
  { id: "methode", label: "Comment estimez-vous aujourd'hui ?", type: "text", weight: 1,
    score: v => (/argus|cote|feeling|tête|habitude/i.test(v || "") ? 2 : 1) },
  { id: "temps", label: "Combien de temps par dossier ?", type: "text", weight: 2,
    score: v => (/h|heure/i.test(v || "") ? 3 : 1) },
  { id: "outils", label: "Quels outils utilisez-vous ?", type: "text", weight: 1, score: () => 1 },
  { id: "frustrations", label: "Principales frustrations ?", type: "text", weight: 2,
    score: v => ((v || "").length > 20 ? 3 : (v || "").length > 0 ? 1 : 0) },
  { id: "besoin_ia", label: "Déjà utilisé une IA dans votre activité ?", type: "text", weight: 1,
    score: v => (/oui|chatgpt|claude|copilot/i.test(v || "") ? 2 : 1) },
  { id: "timing", label: "Échéance envisagée ?", type: "text", weight: 3,
    score: v => (/immédiat|urgent|semaine|maintenant/i.test(v || "") ? 3 : /mois/i.test(v || "") ? 2 : 0) },
  { id: "decisionnaire", label: "Êtes-vous décisionnaire ?", type: "text", weight: 3,
    score: v => (/oui|moi|gérant|dirigeant/i.test(v || "") ? 3 : 0) },
];

// §61 — Le score s'appuie sur les réponses réellement saisies, et il dit
// pourquoi. Un score sans motif n'aide personne à décider quoi faire ensuite.
function computeQualifScore(answers) {
  let got = 0, max = 0;
  const reasons = [];
  for (const q of QUALIF) {
    max += q.weight * 3;
    const raw = answers[q.id];
    if (raw === undefined || raw === "") continue;
    const v = q.type === "number" ? Number(raw) : String(raw);
    const s = q.score(v);
    got += q.weight * s;
    if (q.weight >= 3 && s >= 3) reasons.push(`${q.label.replace(/\s*\?$/, "")} : signal fort`);
    if (q.weight >= 3 && s === 0 && raw !== "") reasons.push(`${q.label.replace(/\s*\?$/, "")} : point bloquant`);
  }
  const pct = max ? Math.round((got / max) * 100) : 0;
  const level = pct >= 70 ? "Très fort" : pct >= 50 ? "Fort" : pct >= 30 ? "Moyen" : "Faible";
  return { level, pct, reasons };
}

const SCRIPTS = {
  calling: {
    objectif: "Obtenir l'attention et identifier le besoin en moins de deux minutes.",
    accroche: "Bonjour, [prénom] de VehicleIQ. Je travaille avec des professionnels du VO qui passent trop de temps à qualifier leurs dossiers. Vous en traitez combien par mois environ ?",
    questions: [
      "Combien de véhicules passent chez vous chaque mois ?",
      "Qui s'occupe de l'estimation et du contrôle aujourd'hui ?",
      "Combien de temps ça vous prend, honnêtement ?",
    ],
    objections: [
      ["« Je n'ai pas le temps »", "Deux minutes, et si ça ne vous parle pas je raccroche. Combien de VO par mois ?"],
      ["« On a déjà un outil »", "Lequel ? Je veux surtout savoir ce qu'il ne fait pas pour vous."],
      ["« Envoyez-moi un mail »", "Je le fais. Avant ça, une question : c'est vous qui décidez sur ce type d'outil ?"],
    ],
    suite: "Caler 15 minutes de démonstration, ou transmettre au closing si le besoin est déjà clair.",
  },
  qualification: {
    objectif: "Comprendre l'activité réelle et mesurer l'urgence.",
    accroche: "Je vais vous poser quelques questions pour voir si VehicleIQ a du sens chez vous. Si ce n'est pas le cas, je vous le dirai.",
    questions: QUALIF.map(q => q.label),
    objections: [
      ["« Ça coûte combien ? »", "Ça dépend de votre volume. Justement, combien de VO par mois ?"],
      ["« On verra plus tard »", "Qu'est-ce qui devrait changer d'ici là pour que ce soit le bon moment ?"],
    ],
    suite: "Renseigner les réponses ci-dessous, puis transmettre au closing.",
  },
  closing: {
    objectif: "Transformer un besoin identifié en souscription.",
    accroche: "On a vu que vous traitez [volume] véhicules par mois et que [frustration] vous coûte du temps. Je vous montre comment VIQ.AI traite ça, et vous me dites si on y va.",
    questions: [
      "Qu'est-ce qui vous retient de démarrer aujourd'hui ?",
      "Vous préférez commencer seul ou avec vos collaborateurs ?",
      "On démarre sur quelle date ?",
    ],
    objections: [
      ["« C'est cher »", "Comparé à quoi ? Un dossier mal estimé coûte plus que l'abonnement mensuel."],
      ["« Je dois en parler »", "À qui, et quand ? Je peux préparer ce dont cette personne a besoin."],
      ["« Et si ça ne marche pas ? »", "Sans engagement, résiliable à tout moment. Vous jugez sur vos propres véhicules."],
    ],
    suite: "Enregistrer la souscription avec le code partenaire.",
  },
};

function ScriptPanel({ mode, answers, setAnswers, onHandoff }) {
  const sc = SCRIPTS[mode];
  const score = mode === "qualification" ? computeQualifScore(answers) : null;

  return (
    <div className="aff-card">
      <div className="aff-sub" style={{ marginTop: 0 }}>Objectif</div>
      <div className="viq-answer" style={{ fontSize: ".88rem" }}>{sc.objectif}</div>

      <div className="aff-sub">Accroche</div>
      <div className="aff-script-quote">{sc.accroche}</div>

      <div className="aff-sub">Questions</div>
      {mode === "qualification" ? (
        <>
          {QUALIF.map(q => (
            <label key={q.id} className="aff-field">
              <span className="aff-label">{q.label}</span>
              <input type={q.type === "number" ? "number" : "text"}
                value={answers[q.id] ?? ""}
                onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} />
            </label>
          ))}
          {score && (
            <div className={`aff-score ${score.level.toLowerCase().replace(/\s/g, "-")}`}>
              <strong>Qualification : {score.level}</strong> · {score.pct} %
              {score.reasons.length > 0 && (
                <ul className="viq-bullets">
                  {score.reasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              {!score.reasons.length && (
                <div className="aff-hint">Renseignez le volume, l'échéance et le décisionnaire : ce sont les trois signaux qui pèsent le plus.</div>
              )}
            </div>
          )}
          <button className="viq-btn primary" onClick={() => onHandoff(answers, score)}>
            Transmettre au closing
          </button>
        </>
      ) : (
        <ul className="viq-bullets">{sc.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
      )}

      <div className="aff-sub">Objections</div>
      <div className="viq-rows">
        {sc.objections.map(([o, r], i) => (
          <div key={i} className="viq-row" style={{ alignItems: "flex-start" }}>
            <span className="k" style={{ minWidth: 150 }}>{o}</span>
            <span className="v" style={{ fontWeight: 400, textAlign: "left" }}>{r}</span>
          </div>
        ))}
      </div>

      <div className="aff-sub">Prochaine action</div>
      <div className="aff-hint">{sc.suite}</div>
    </div>
  );
}

export default function AffiliateMember({ onClose, embedded = false }) {
  // En mode intégré, le composant vit DANS l'ancien dashboard apporteur : il
  // n'affiche que ses revenus, sans réinventer un en-tête ni des onglets qui
  // doubleraient ceux du parent.
  const [tab, setTab] = useState("revenus");
  const [data, setData] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [mode, setMode] = useState("calling");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [m, p] = await Promise.all([
      apiGet("/api/affiliate-member/me").catch(() => null),
      apiGet("/api/affiliate-member/pipeline").catch(() => null),
    ]);
    setData(m || null); setPipeline(p || null); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handoff = async (ans, score) => {
    const j = await apiPost("/api/affiliate-member/handoff", {
      client_uid: ans.client_uid || null,
      code: data?.codes?.[0]?.code || null,
      qualification: ans,
      qualification_score: score?.level || null,
      notes: ans.frustrations || null,
    }).catch(e => ({ error: e.message }));
    if (j?.success) { setFlash("Transmis au closing. Le closer retrouvera la qualification sans rien ressaisir."); setAnswers({}); load(); }
    else setFlash(j?.error || "Transmission impossible.");
  };

  if (loading) return <div className="aff-admin viq-dark"><div className="viq-empty">Chargement…</div></div>;

  if (!data?.member) {
    return (
      <div className="aff-admin viq-dark">
        <div className="aff-head"><h2>Programme partenaires</h2>
          {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}</div>
        <div className="viq-empty">
          Votre compte n'est pas encore rattaché au programme partenaires.
          Contactez VehicleIQ pour obtenir un code.
        </div>
      </div>
    );
  }

  const { member, stats, codes, months, sales } = data;
  const dueCents = (months || []).filter(m => m.status !== "paid")
    .reduce((s, m) => s + (m.commission_cents || 0), 0);
  const paidCents = (months || []).filter(m => m.status === "paid")
    .reduce((s, m) => s + (m.commission_cents || 0), 0);

  return (
    <div className={embedded ? "aff-admin aff-embedded" : "aff-admin viq-dark"}>
      {!embedded && (
        <div className="aff-head">
          <h2>{member.name} — {ROLE_LABELS[member.role] || member.role}</h2>
          {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}
        </div>
      )}

      <div className="aff-tabs">
        {[["revenus", "Mes revenus"], ["pipeline", "Mon pipeline"], ["script", "Script commercial"]].map(([k, l]) => (
          <button key={k} className={`aff-tab${tab === k ? " on" : ""}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {flash && <div className="aff-sim" style={{ marginBottom: ".8rem" }}>{flash}</div>}

      {tab === "revenus" && (
        <>
          <div className="aff-card">
            <div className="viq-rows">
              <div className="viq-row"><span className="k">Ventes où vous êtes intervenu</span><span className="v">{stats.sales}</span></div>
              <div className="viq-row"><span className="k">CA influencé</span><span className="v">{eur(stats.pool_influenced_cents ?? stats.pool_cents)}</span></div>
              <div className="viq-row"><span className="k">Part moyenne du pool</span><span className="v">{Number(stats.avg_split || 0).toFixed(0)} %</span></div>
              <div className="viq-row"><span className="k">Commission versée</span><span className="v">{eur(paidCents)}</span></div>
              <div className="viq-row"><span className="k">Commission à venir</span><span className="v pos">{eur(dueCents)}</span></div>
              <div className="viq-row total"><span className="k">Commission générée</span><span className="v">{eur(stats.commission_cents)}</span></div>
            </div>
          </div>

          <div className="aff-sub">Vos codes</div>
          {codes.map(c => (
            <div key={c.code} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-code">{c.code}</span>
                <span className="aff-name">{c.name}</span>
                {!c.active && <span className="viq-chip warn">désactivé</span>}
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">Votre rôle</span><span className="v">{ROLE_LABELS[c.role] || c.role}</span></div>
                <div className="viq-row"><span className="k">Votre part du pool</span><span className="v">{Number(c.split_pct)} %</span></div>
                {c.renewal_split_pct != null && (
                  <div className="viq-row"><span className="k">Votre part sur renouvellement</span><span className="v">{Number(c.renewal_split_pct)} %</span></div>
                )}
                {c.participants_count > 1 && (
                  <div className="viq-row">
                    <span className="k">Code partagé</span>
                    <span className="v">{c.participants_count} participants</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="aff-sub">Détail des ventes</div>
          {!sales.length && <div className="viq-empty">Aucune vente pour le moment.</div>}
          {sales.map((s, i) => (
            <div key={i} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{s.plan_id || "Abonnement"}</span>
                <span className="viq-chip">{s.sale_type === "recurrence" ? "Renouvellement" : "Première vente"}</span>
              </div>
              <div className="viq-rows">
                <div className="viq-row"><span className="k">Montant de la vente</span><span className="v">{eur(s.amount_cents)}</span></div>
                <div className="viq-row"><span className="k">Commission globale</span><span className="v">{eur(s.pool_cents)}</span></div>
                <div className="viq-row"><span className="k">Votre rôle</span><span className="v">{ROLE_LABELS[s.role] || s.role}</span></div>
                <div className="viq-row"><span className="k">Votre part</span><span className="v">{Number(s.split_pct_used)} %</span></div>
                <div className="viq-row total"><span className="k">Votre commission</span><span className="v">{eur(s.commission_cents)}</span></div>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "pipeline" && (
        <>
          <div className="aff-card">
            <div className="viq-rows">
              <div className="viq-row"><span className="k">Leads générés</span><span className="v">{pipeline?.kpi?.leads ?? 0}</span></div>
              <div className="viq-row"><span className="k">Qualifiés</span><span className="v">{pipeline?.kpi?.qualifies ?? 0}</span></div>
              <div className="viq-row"><span className="k">Signatures</span><span className="v pos">{pipeline?.kpi?.signatures ?? 0}</span></div>
              {pipeline?.kpi?.taux_qualification != null && (
                <div className="viq-row"><span className="k">Taux de qualification</span><span className="v">{pipeline.kpi.taux_qualification} %</span></div>
              )}
              {pipeline?.kpi?.taux_closing != null && (
                <div className="viq-row"><span className="k">Taux de closing</span><span className="v">{pipeline.kpi.taux_closing} %</span></div>
              )}
            </div>
          </div>
          {!pipeline?.deals?.length && <div className="viq-empty">Aucune opportunité suivie pour l'instant.</div>}
          {(pipeline?.deals || []).map(d => (
            <div key={d.id} className="aff-card">
              <div className="aff-card-head">
                <span className="aff-name">{d.client_uid || `Lead #${d.lead_id}`}</span>
                <span className="viq-chip">{d.stage}</span>
                {d.qualification_score && <span className="viq-chip accent">{d.qualification_score}</span>}
              </div>
              <div className="viq-rows">
                {d.caller_name && <div className="viq-row"><span className="k">Calling</span><span className="v">{d.caller_name}</span></div>}
                {d.closer_name && <div className="viq-row"><span className="k">Closing</span><span className="v">{d.closer_name}</span></div>}
              </div>
            </div>
          ))}
        </>
      )}

      {tab === "script" && (
        <>
          <div className="aff-tabs">
            {[["calling", "1. Calling"], ["qualification", "2. Qualification"], ["closing", "3. Closing"]].map(([k, l]) => (
              <button key={k} className={`aff-tab${mode === k ? " on" : ""}`} onClick={() => setMode(k)}>{l}</button>
            ))}
          </div>
          <ScriptPanel mode={mode} answers={answers} setAnswers={setAnswers} onHandoff={handoff} />
        </>
      )}
    </div>
  );
}
